import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fromAccounts, fromSurvey, useAppDispatch } from '../../../../store';
import { SalesforceAccount, SurveyName } from '../../../../models';
import { LoadingProgress } from '../../../../layout';

interface SurveyIframeProps {
  handleSurveyClose: (action: SurveyAction) => void,
}

interface SurveyMessageResponse {
  action: SurveyAction;
}

export enum SurveyAction {
  FINISH = 'finish',
  CANCEL = 'cancel',
}

export interface SurveyFrame {
  accountId: number;
  sfAccountId: string;
}

// Open immediately: use completed metadata for the selected account when available, otherwise
// keep the existing empty Salesforce-id fallback. Freeze the frame for this opening so a late
// result cannot reload the survey mid-answer or put another account's id into its URL.
export const resolveSurveyFrame = (
  lookup: fromAccounts.SalesforceLookup,
  selectedAccountId: number | undefined,
  salesforceAccount: SalesforceAccount | undefined,
  current: SurveyFrame | undefined,
): SurveyFrame | undefined => {
  if (selectedAccountId === undefined) return undefined;
  if (current?.accountId === selectedAccountId) return current;
  return {
    accountId: selectedAccountId,
    sfAccountId: (lookup.accountId === selectedAccountId
      && lookup.status === 'fulfilled' && salesforceAccount?.id) || '',
  };
};

const SurveyIframe = (props: SurveyIframeProps) => {
  const {
    handleSurveyClose,
  } = props;
  const ref = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  const selectedAccountId = useSelector(fromAccounts.selectSelectedAccountId);
  const lookup = useSelector(fromAccounts.selectSalesforceLookup);
  const salesforceAccount = useSelector(fromAccounts.selectSalesforceAccount);
  const frameRef = useRef<SurveyFrame>();
  const frame = resolveSurveyFrame(lookup, selectedAccountId, salesforceAccount, frameRef.current);
  frameRef.current = frame;

  const {
    surveyURL,
  } = useSelector(fromSurvey.selectSurvey(SurveyName.TURN_OFF_AUTO_RENEW_SURVEY))!!;

  // Populate metadata for a later opening when the page has not started an own-account lookup.
  useEffect(() => {
    if (selectedAccountId !== undefined && lookup.accountId !== selectedAccountId) {
      dispatch(fromAccounts.doGetSalesforceLinkedAccount({ accountId: selectedAccountId }));
    }
  }, [selectedAccountId, lookup.accountId]);

  useEffect(() => {
    setIsLoading(true);
  }, [frame?.accountId]);

  // The listener is installed once, so it must read the latest callback instead of the first
  // render's (FINISH toggles auto-renew with state captured when the callback was created).
  const handleSurveyCloseRef = useRef(handleSurveyClose);
  handleSurveyCloseRef.current = handleSurveyClose;
  const hasClosedRef = useRef(false);

  useEffect(() => {
    const handleMessage = (message: MessageEvent<SurveyMessageResponse | null>) => {
      const action = message.data?.action;
      if (action !== SurveyAction.FINISH && action !== SurveyAction.CANCEL) return;
      // FINISH turns auto-renew off, so only the survey frame rendered here may send it: any
      // other window (another tab, a popup, a foreign frame) can postMessage to this page. The
      // frame's own window is checked rather than its origin because the survey may redirect
      // within the frame. Rejected actions are logged to expose nested-frame vendor posts.
      if (!ref.current || message.source !== ref.current.contentWindow) {
        // eslint-disable-next-line no-console
        console.warn(
          `[survey] ignored ${action} from a window other than the survey frame `
          + `(origin ${message.origin || 'unknown'})`,
        );
        return;
      }
      if (hasClosedRef.current) return;
      hasClosedRef.current = true;
      handleSurveyCloseRef.current(action);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
  };

  return (
    <>
      {(!frame || isLoading) && <LoadingProgress />}
      {frame && (
      <iframe
        key={frame.accountId}
        title="Survey"
        src={`${surveyURL}&account_id=${frame.accountId}&sf_account_id=${frame.sfAccountId}`}
        width="100%"
        height="100%"
        ref={ref}
        style={{
          border: '1px solid #FFFFFF',
          borderRadius: '8px',
          display: isLoading ? 'none' : 'block',
        }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
      )}
    </>
  );
};

export default SurveyIframe;
